import { publicProcedure, createTRPCRouter } from "../trpc"
import { z } from "zod"

export type PurposeCategory = "income" | "expense" | "other"

export const purposesRouter = createTRPCRouter({
  getAll: publicProcedure.query(async ({ ctx }) => {
    const incomePurposes = await ctx.prisma.purpose.findMany({
      where: { category: "income" },
      select: { name: true },
    })

    const expensePurposes = await ctx.prisma.purpose.findMany({
      where: { category: "expense" },
      select: { name: true },
    })

    const otherPurposes = await ctx.prisma.purpose.findMany({
      where: { category: "other" },
      select: { name: true },
    })

    return {
      income: incomePurposes.map((p) => p.name),
      expense: expensePurposes.map((p) => p.name),
      other: otherPurposes.map((p) => p.name),
    }
  }),

  add: publicProcedure
    .input(
      z.object({
        category: z.enum(["income", "expense", "other"] as [PurposeCategory, ...PurposeCategory[]]),
        purpose: z.string().min(1).max(100),
      }),
    )
    .mutation(async ({ ctx, input }) => {
      try {
        await ctx.prisma.purpose.create({
          data: {
            name: input.purpose,
            category: input.category,
          },
        })
        return { success: true }
      } catch (error) {
        console.error("Error adding purpose:", error)
        return {
          success: false,
          error: "Failed to add purpose. It may already exist.",
        }
      }
    }),

  update: publicProcedure
    .input(
      z.object({
        category: z.enum(["income", "expense", "other"] as [PurposeCategory, ...PurposeCategory[]]),
        oldPurpose: z.string().min(1),
        newPurpose: z.string().min(1).max(100),
      }),
    )
    .mutation(async ({ ctx, input }) => {
      try {
        await ctx.prisma.purpose.updateMany({
          where: {
            name: input.oldPurpose,
            category: input.category,
          },
          data: {
            name: input.newPurpose,
          },
        })
        return { success: true }
      } catch (error) {
        console.error("Error updating purpose:", error)
        return {
          success: false,
          error: "Failed to update purpose.",
        }
      }
    }),

  delete: publicProcedure
    .input(
      z.object({
        category: z.enum(["income", "expense", "other"] as [PurposeCategory, ...PurposeCategory[]]),
        purpose: z.string().min(1),
      }),
    )
    .mutation(async ({ ctx, input }) => {
      try {
        await ctx.prisma.purpose.deleteMany({
          where: {
            name: input.purpose,
            category: input.category,
          },
        })
        return { success: true }
      } catch (error) {
        console.error("Error deleting purpose:", error)
        return {
          success: false,
          error: "Failed to delete purpose.",
        }
      }
    }),
})
